/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
export enum RoomStatus {
  CREATED = 'CREATED',
  CANCELED = 'CANCELED',
  COMPLETED = 'COMPLETED',
  NEEDS_ATTENTION = 'NEEDS_ATTENTION',
  DRAFT = 'DRAFT',
  PAUSED = 'PAUSED',
}

export enum AssignmentStatus {
  PENDING = 'PENDING',
  DONE = 'DONE',
}

export enum AccessRoomError {
  PARTICIPANT_JOINING_BEFORE_SCHEDULED_START = 'Cannot join before the scheduled time.',
  PARTICIPANT_JOINING_AFTER_SCHEDULED_END = 'Cannot join after the scheduled time.',
  PARTICIPANT_JOINING_BEFORE_ROOM_SCHEDULED = 'Cannot join before room is scheduled.',
}

export const TerminalRoomStatuses = [RoomStatus.CANCELED, RoomStatus.COMPLETED, RoomStatus.NEEDS_ATTENTION];

/**
 * The SignAheadDocsStatus change from PENDING_BORROWER_SIGNATURE to SIGNED when all required sign ahead docs signatures
 * have been successfully collected from borrower side.
 */
export enum SignAheadDocsStatus {
  PENDING_BORROWER_SIGNATURE = 'PENDING_BORROWER_SIGNATURE',
  SIGNED = 'SIGNED',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
}

export const SIGN_AHEAD_DOCS_STATUS_ALL = Object.values(SignAheadDocsStatus);

// Rooms will be associated with a group based on settlement agent email domain.
// This has been stored here for now. If the developer adding to this list
// feels it is getting too large, they should move to a better storage location.
export const settlementAgentDomainToGroupName = new Map<string, string>([
  ['blendnotary.com', 'Blend Notary'],
  ['blend.com', 'Blend Notary'],
  ['acttitleohio.com', 'ServiceLink'],
  ['aggielandtitle.com', 'ServiceLink'],
  ['alamotitle.com', 'ServiceLink'],
  ['allegiancetitle.com', 'ServiceLink'],
  ['allfirst.co', 'ServiceLink'],
  ['amerititle.net', 'ServiceLink'],
  ['austintitle.com', 'ServiceLink'],
  ['avescrowservices.com', 'ServiceLink'],
  ['bancserv.net', 'ServiceLink'],
  ['barristerstitle.com', 'ServiceLink'],
  ['bayshoretitle.com', 'ServiceLink'],
  ['belletitlellc.com', 'ServiceLink'],
  ['brtitle.com', 'ServiceLink'],
  ['chartertitle.com', 'ServiceLink'],
  ['chelseatitle.net', 'ServiceLink'],
  ['closedescrowinc.com', 'ServiceLink'],
  ['closingserviceswi.com', 'ServiceLink'],
  ['cltic.com', 'ServiceLink'],
  ['cltlt.com', 'ServiceLink'],
  ['columbiatitlecompany.com', 'ServiceLink'],
  ['commandescrow.com', 'ServiceLink'],
  ['cptitle.com', 'ServiceLink'],
  ['cticga.com', 'ServiceLink'],
  ['ctt.com', 'ServiceLink'],
  ['danecountytitle.com', 'ServiceLink'],
  ['digisolaz.com', 'ServiceLink'],
  ['elementsescrow.com', 'ServiceLink'],
  ['emeraldescrow.com', 'ServiceLink'],
  ['enterprisetitle.com', 'ServiceLink'],
  ['exceltitlegroup.com', 'ServiceLink'],
  ['firstitle.com', 'ServiceLink'],
  ['firstusatitleagency.com', 'ServiceLink'],
  ['floridaclosingco.com', 'ServiceLink'],
  // Note: fnf.com has tenant-specific routing: bmo tenant -> Fidelity, all other tenants -> ServiceLink
  ['fnf.com', 'Fidelity'],
  ['fnfrlc.com', 'ServiceLink'],
  ['FNTG.com', 'ServiceLink'],
  ['formaro.com', 'ServiceLink'],
  ['frsonline.com', 'ServiceLink'],
  ['gatewaytitleme.com', 'ServiceLink'],
  ['gcta.com', 'ServiceLink'],
  ['gmtitle.net', 'ServiceLink'],
  ['guarantytitlenm.com', 'ServiceLink'],
  ['heritagetco.com', 'ServiceLink'],
  ['hltco.com', 'ServiceLink'],
  ['huntingtontitle.net', 'ServiceLink'],
  ['landmarktitlekenosha.com', 'ServiceLink'],
  ['leadershiptitlecompany.com', 'ServiceLink'],
  ['lgtitleservices.com', 'ServiceLink'],
  ['libtitle.com', 'ServiceLink'],
  ['lincolntitlecompany.com', 'ServiceLink'],
  ['loancare.com', 'ServiceLink'],
  ['lptitletx.com', 'ServiceLink'],
  ['ltic.com', 'ServiceLink'],
  ['majestytitle.com', 'ServiceLink'],
  ['masterpiecetitle.com', 'ServiceLink'],
  ['metesandboundstitle.com', 'ServiceLink'],
  ['novarenss.com', 'ServiceLink'],
  ['oxfordcommercialtitle.com', 'ServiceLink'],
  ['parkstitle.com', 'ServiceLink'],
  ['pc-escrow.com', 'ServiceLink'],
  ['professionaltitle.com', 'ServiceLink'],
  ['proptitle.com', 'ServiceLink'],
  ['ptitleescrow.com', 'ServiceLink'],
  ['relianttitlegroup.com', 'ServiceLink'],
  ['sanjuantitle.com', 'ServiceLink'],
  ['securedescrowinc.com', 'ServiceLink'],
  ['SecurityTitle.com', 'ServiceLink'],
  ['servicelinkauction.com', 'ServiceLink'],
  ['setitle.com', 'ServiceLink'],
  ['shalimar-escrow.com', 'ServiceLink'],
  ['signaturetitle.com', 'ServiceLink'],
  ['smithbrothersabstract.com', 'ServiceLink'],
  ['startextitle.com', 'ServiceLink'],
  ['svclnk.com', 'ServiceLink'],
  ['synergytitlefl.com', 'ServiceLink'],
  ['tghawaii.com', 'ServiceLink'],
  ['theescrowexperts.com', 'ServiceLink'],
  ['ticorescrow.com', 'ServiceLink'],
  ['ticortitle.com', 'ServiceLink'],
  ['titlegroup.fntg.com', 'ServiceLink'],
  ['titlelogixsolutions.com', 'ServiceLink'],
  ['title-management.com', 'ServiceLink'],
  ['tridenttitlellc.com', 'ServiceLink'],
  ['ultraescrow.com', 'ServiceLink'],
  ['versalescrow.com', 'ServiceLink'],
  ['villageescrowservices.com', 'ServiceLink'],
  ['westerntitle.com', 'ServiceLink'],
  ['woodlandohio.com', 'ServiceLink'],
  ['wtabstract.com', 'ServiceLink'],
  ['compeer.com', 'Compeer'],
  ['usbank.com', 'US Bank'],
  ['radian.com', 'US Bank'],
  ['gmail.com', 'Test Gmail'], // This is a test group only applicable in sandbox
]);

// Represents the list of notary groups that are owned by Blend
export const internalNotaryGroups: string[] = ['Blend Notary'];

export enum defaultNotaryTaskTypes {
  LENDER_DOCS_STATUS = 'Lender Documents',
  TITLE_DOCS_STATUS = 'Title Documents',
}

export const DEFAULT_NOTARY_TASKS_ALL = Object.values(defaultNotaryTaskTypes);

export const notaryTaskToDefaultStatusMapping: Record<defaultNotaryTaskTypes, string> = {
  'Lender Documents': 'Pending Tagging',
  'Title Documents': 'Pending Docs',
};

export enum FeedbackUserType {
  NOTARY = 'NOTARY',
  PARTICIPANT = 'PARTICIPANT',
}

export enum FeedbackIssueType {
  ID_VERIFICATION = 'ID_VERIFICATION',
  DOCUMENT_SIGNING = 'DOCUMENT_SIGNING',
  VIDEO_AUDIO_QUALITY = 'VIDEO_AUDIO_QUALITY',
}

export const FEEDBACK_USER_TYPE_ALL = Object.values(FeedbackUserType);

export const FEEDBACK_ISSUE_TYPE_ALL = Object.values(FeedbackIssueType);

export enum AppointmentStatus {
  PENDING_PREFERENCES = 'PENDING_PREFERENCES',
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
  TIME_CONFIRMED = 'TIME_CONFIRMED',
}

export enum LenderDocuments {
  INCOMPLETE_OR_MISSING = 'Incomplete/Missing',
  PENDING_TAGGING = 'Pending Tagging',
  TAGGED = 'Tagged',
}

export enum TitleDocuments {
  PENDING_DOCS = 'Pending Docs',
  RECEIVED_AND_TAGGED = 'Received and Tagged',
  PENDING_TAGGING = 'Pending Tagging',
}
